import { useEffect, useState } from "react";
import { ethers } from "ethers";
import "./index.css";

const PRVX_TOKEN = "0x700509775B89e6695Da271c79c976d65846A0180";
const STAKING_CONTRACT = "0xb26c14670ac650E4DB419033Ab54251682d22958";

function App() {
  const [wallet, setWallet] = useState(null);
  const [provider, setProvider] = useState(null);
  const [prvx, setPrvx] = useState(null);
  const [staking, setStaking] = useState(null);
  const [balance, setBalance] = useState("0");
  const [staked, setStaked] = useState("0");
  const [rewards, setRewards] = useState("0");
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(true);

  const loadData = async (wallet, prvx, staking) => {
    const user = ethers.getAddress(wallet);
    const [bal, stk, rew] = await Promise.all([
      prvx.balanceOf(user),
      staking.stakes(user),
      staking.pendingRewards(user),
    ]);
    return {
      balance: ethers.formatUnits(bal, 18),
      staked: ethers.formatUnits(stk, 18),
      rewards: ethers.formatUnits(rew, 18),
    };
  };

  useEffect(() => {
    if (provider && wallet) {
      const loadContracts = async () => {
        const signer = await provider.getSigner();
        const token = new ethers.Contract(PRVX_TOKEN, [
          "function balanceOf(address) view returns (uint256)",
          "function approve(address,uint256) returns (bool)",
        ], signer);

        const stakingContract = new ethers.Contract(STAKING_CONTRACT, [
          "function stake(uint256) external",
          "function unstake(uint256) external",
          "function claimRewards() external",
          "function stakes(address) view returns (uint256)",
          "function pendingRewards(address) view returns (uint256)",
        ], signer);

        setPrvx(token);
        setStaking(stakingContract);
      };
      loadContracts();
    }
  }, [provider, wallet]);

  useEffect(() => {
    const update = async () => {
      if (!prvx || !staking || !wallet) return;
      const { balance, staked, rewards } = await loadData(wallet, prvx, staking);
      setBalance(balance);
      setStaked(staked);
      setRewards(rewards);
    };
    update();
  }, [prvx, staking, wallet]);

  const connectWallet = async () => {
    if (window.ethereum) {
      const prov = new ethers.BrowserProvider(window.ethereum);
      const accs = await window.ethereum.request({ method: "eth_requestAccounts" });
      const net = await prov.getNetwork();

      if (net.chainId !== 1n) {
        alert("Please switch to Ethereum Mainnet.");
        return;
      }

      setWallet(accs[0]);
      setProvider(prov);
    } else {
      alert("Install Metamask!");
    }
  };

  const handleStake = async () => {
    try {
      setLoading(true);
      setMessage("Staking in progress...");
      const parsed = ethers.parseUnits(amount, 18);
      await prvx.approve(STAKING_CONTRACT, parsed);
      await staking.stake(parsed);
      const updated = await loadData(wallet, prvx, staking);
      setBalance(updated.balance);
      setStaked(updated.staked);
      setRewards(updated.rewards);
      setMessage("✅ Staked successfully!");
    } catch {
      setMessage("❌ Staking failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleUnstake = async () => {
    try {
      setLoading(true);
      setMessage("Unstaking in progress...");
      const parsed = ethers.parseUnits(amount, 18);
      await staking.unstake(parsed);
      await staking.claimRewards();
      const updated = await loadData(wallet, prvx, staking);
      setBalance(updated.balance);
      setStaked(updated.staked);
      setRewards(updated.rewards);
      setMessage("✅ Unstaked and rewards claimed!");
    } catch {
      setMessage("❌ Unstaking failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    try {
      setLoading(true);
      setMessage("Claiming rewards...");
      await staking.claimRewards();
      const updated = await loadData(wallet, prvx, staking);
      setBalance(updated.balance);
      setStaked(updated.staked);
      setRewards(updated.rewards);
      setMessage("✅ Rewards claimed!");
    } catch {
      setMessage("❌ Claim failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${darkMode ? "bg-black text-white" : "bg-white text-black"} min-h-screen p-6`}>
      <div className="max-w-xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-[#4befa0]">PRVX Staking App</h1>
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="border px-3 py-1 text-sm rounded-lg"
          >
            {darkMode ? "☀ Light" : "🌙 Dark"}
          </button>
        </div>

        {!wallet ? (
          <div className="text-center space-y-4">
            <p className="text-lg">Interface loaded. Connect your wallet to begin.</p>
            <button
              className="bg-[#4befa0] text-black px-4 py-2 rounded-xl"
              onClick={connectWallet}
            >
              Connect Wallet
            </button>
          </div>
        ) : (
          <>
            <div className="mb-4 space-y-1 text-sm">
              <p><strong>👛 Wallet:</strong> {wallet}</p>
              <p><strong>Balance:</strong> {balance} PRVX</p>
              <p><strong>Staked:</strong> {staked} PRVX</p>
              <p><strong>Rewards:</strong> {rewards} PRVX</p>
            </div>

            {message && (
              <p className={`mb-2 text-sm ${message.startsWith("✅") ? "text-green-500" : message.startsWith("❌") ? "text-red-500" : "text-yellow-500"}`}>
                {message}
              </p>
            )}

            {loading && (
              <div className="flex justify-center mb-4">
                <div className="w-6 h-6 border-4 border-[#4befa0] border-dashed rounded-full animate-spin"></div>
              </div>
            )}

            <div className="flex mb-4">
              <input
                type="text"
                placeholder="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={`flex-grow p-2 rounded-l-xl ${darkMode ? "bg-[#1e1e1e] text-white" : "bg-gray-200 text-black"} focus:outline-none`}
              />
              <button
                onClick={() => setAmount(balance)}
                className="bg-[#4befa0] text-black px-4 rounded-r-xl"
              >
                Max
              </button>
            </div>

            <div className="flex flex-col space-y-2 mb-6">
              <button className="bg-[#4befa0] text-black py-2 rounded-xl" onClick={handleStake} disabled={loading}>Stake</button>
              <button className="bg-gray-700 text-white py-2 rounded-xl" onClick={handleUnstake} disabled={loading}>Unstake</button>
              <button className="bg-emerald-700 text-white py-2 rounded-xl" onClick={handleClaim} disabled={loading}>Claim Rewards</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default App;

